// Resolving a tutorial step to a DOM element — shared by the `tutorial` and `tutorial-config` controllers.
//
// Anchors: data-tour="<partial>.<element>", e.g. in partials/_lead_row.html:
//   <article data-tour="lead-row"> … <div data-tour="lead-row.add"> …
// partial = file name without "_" and extension (kebab-case); a partial's root is just the name.
//
// Step:
//   { anchor: "lead-filters" }                              a single element
//   { anchor: "lead-row", nth: 3 }                          the 3rd VISIBLE row
//   { anchor: "lead-row.add", scope: "lead-row", nth: 3 }   the element INSIDE the 3rd row (nth counts rows, so
//                                                           hidden elements in other rows never shift it)
//   nth = 1 with a scope → the first row in which the element is visible.

export const ATTR = "data-tour"

// jsdom has no CSS.escape; anchors are kebab/dot words, so escaping quotes and backslashes is enough.
const sel = (anchor) => `[${ATTR}="${String(anchor).replace(/["\\]/g, "\\$&")}"]`

// visibility:hidden keeps client rects (the closed mobile filter sheet) — it is not a target either.
export const visible = (el) =>
  !!el && !el.closest("template") && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden"

export function instances(doc, anchor) {
  return anchor ? [...doc.querySelectorAll(sel(anchor))].filter(visible) : []
}

export function resolve(doc, step) {
  if (!step) return null
  if (step.anchor && step.scope) {
    const scopes = instances(doc, step.scope)
    const n = step.nth || 1
    if (n === 1) {
      for (const scope of scopes) {
        const el = scope.querySelector(sel(step.anchor))
        if (visible(el)) return el
      }
      return null
    }
    const el = scopes[n - 1]?.querySelector(sel(step.anchor))
    return visible(el) ? el : null
  }
  if (step.anchor) return instances(doc, step.anchor)[(step.nth || 1) - 1] || null
  if (step.target) return doc.querySelector(`[id="${String(step.target).replace(/["\\]/g, "\\$&")}"]`)
  return null
}

// `doc` in resolve/instances/catalog is a Document OR an Element: the inline configurator preview and the
// `tutorial` controller inside it look anchors up in that container only, never in the surrounding UI.
//
// The anchor chain from the outermost ancestor down to the element (`root`: the container, default the document).
// Each entry: { el, anchor, nth, total, scope?, scopeNth?, scopeTotal? }
//   scope = the nearest REPEATING anchor ancestor; entries below it are numbered by that ancestor.
export function chain(el, root = null) {
  const doc = root || el.ownerDocument
  const raw = []
  let node = el.closest(`[${ATTR}]`)
  while (node && (!root || root.contains(node))) {
    const anchor = node.getAttribute(ATTR)
    const all = instances(doc, anchor)
    if (all.length) raw.unshift({ el: node, anchor, nth: all.indexOf(node) + 1, total: all.length })
    node = node.parentElement?.closest(`[${ATTR}]`)
  }
  let repeating = null
  for (const entry of raw) {
    if (repeating) Object.assign(entry, { scope: repeating.anchor, scopeNth: repeating.nth, scopeTotal: repeating.total })
    if (entry.total > 1 && !repeating) repeating = entry
  }
  return raw
}

// A step from a chain entry; specific=false → always the first occurrence.
export function stepFromEntry(entry, specific) {
  if (entry.scope) return { anchor: entry.anchor, scope: entry.scope, nth: specific ? entry.scopeNth : 1 }
  return { anchor: entry.anchor, nth: entry.total > 1 && specific ? entry.nth : 1 }
}

// How many occurrences nth counts for a step (rows when it has a scope).
export function countFor(doc, step) {
  return step?.scope ? instances(doc, step.scope).length : instances(doc, step?.anchor).length
}

// Every visible anchor of the page with its occurrence count, sorted.
export function catalog(doc) {
  const counts = new Map()
  doc.querySelectorAll(`[${ATTR}]`).forEach((el) => {
    if (!visible(el)) return
    const anchor = el.getAttribute(ATTR)
    counts.set(anchor, (counts.get(anchor) || 0) + 1)
  })
  return [...counts].map(([anchor, total]) => ({ anchor, total })).sort((a, b) => a.anchor.localeCompare(b.anchor))
}

export function label(step) {
  if (step?.anchor && step.scope) {
    const child = step.anchor.startsWith(`${step.scope}.`) ? step.anchor.slice(step.scope.length + 1) : step.anchor
    return `${step.scope} ${(step.nth || 1) === 1 ? "pierwszy" : `${step.nth}.`} › ${child}`
  }
  if (step?.anchor) return step.anchor + ((step.nth || 1) > 1 ? ` · ${step.nth}.` : "")
  if (step?.target) return `#${step.target}`
  return "—"
}
